// src/ui/PollCreator.jsx — compose a poll.
import React, { useEffect, useRef, useState } from 'react';
import { X, Poll, Plus, Trash, Send } from './icons';

const MAX_OPTIONS = 12;

export default function PollCreator({ isGroup, onClose, onCreate }) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [multiple, setMultiple] = useState(false);
  const [privateVoting, setPrivateVoting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const firstRef = useRef(null);
  const lastAddedRef = useRef(null);

  useEffect(() => { firstRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filled = options.map((o) => o.trim()).filter(Boolean);
  const ready = question.trim().length > 0 && filled.length >= 2;

  const setOpt = (i, v) => setOptions((prev) => prev.map((o, j) => (j === i ? v : o)));
  const addOpt = () => setOptions((prev) => (prev.length >= MAX_OPTIONS ? prev : [...prev, '']));
  const removeOpt = (i) => setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((_, j) => j !== i)));

  const submit = async () => {
    if (!ready || sending) return;
    setSending(true); setError('');
    try {
      await onCreate({ question: question.trim(), options: filled, multipleChoice: multiple, privateVoting });
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not create the poll.');
      setSending(false);
    }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal poll-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head loc-head">
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Poll width={17} height={17} /> Create a poll</span>
          <button className="icon-btn" onClick={onClose} title="Close"><X width={18} height={18} /></button>
        </div>

        <div className="modal-body poll-body">
          <label className="pc-label" htmlFor="pc-q">Question</label>
          <textarea
            id="pc-q"
            ref={firstRef}
            className="pc-question"
            rows={2}
            maxLength={300}
            placeholder="Ask something…"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />

          <div className="pc-label-row">
            <span className="pc-label">Options</span>
            <span className="pc-count">{filled.length}/{MAX_OPTIONS}</span>
          </div>

          {options.map((o, i) => (
            <div className="pc-opt" key={i}>
              <span className="pc-bullet">{i + 1}</span>
              <input
                ref={i === options.length - 1 ? lastAddedRef : null}
                value={o}
                maxLength={120}
                placeholder={`Option ${i + 1}`}
                onChange={(e) => setOpt(i, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); if (i === options.length - 1) { addOpt(); setTimeout(() => lastAddedRef.current?.focus(), 0); } }
                }}
              />
              {options.length > 2 && (
                <button className="icon-btn pc-x" onClick={() => removeOpt(i)} title="Remove"><Trash width={15} height={15} /></button>
              )}
            </div>
          ))}

          {options.length < MAX_OPTIONS && (
            <button className="pc-add" onClick={() => { addOpt(); setTimeout(() => lastAddedRef.current?.focus(), 0); }}>
              <Plus width={15} height={15} /> Add option
            </button>
          )}

          <div className="pc-toggles">
            <label className="pc-toggle">
              <input type="checkbox" checked={multiple} onChange={(e) => setMultiple(e.target.checked)} />
              <span><b>Allow multiple answers</b><em>People can pick more than one option</em></span>
            </label>
            {isGroup && (
              <label className="pc-toggle">
                <input type="checkbox" checked={privateVoting} onChange={(e) => setPrivateVoting(e.target.checked)} />
                <span><b>Private voting</b><em>Hide who voted for what</em></span>
              </label>
            )}
          </div>

          {error ? <div className="loc-error">{error}</div> : null}
        </div>

        <div className="modal-foot">
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={!ready || sending}>
            <Send width={15} height={15} style={{ marginRight: 6, verticalAlign: -2 }} />
            {sending ? 'Creating…' : 'Send poll'}
          </button>
        </div>
      </div>
    </div>
  );
}
